import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
  Input,
  OnDestroy,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { Observable, Subject, Subscription } from "rxjs";
import * as _ from "underscore";
import { RootStoreState } from "src/app/root-store";
import { takeUntil } from "rxjs/operators";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";

import { FloorplannerService } from "src/app/services/floorplanner.service";
import {
  FloorplannerAPI,
  FloorplannerProject,
  FloorplannerAsset,
} from "src/app/models/Floorplanner.model";

import { UnitSizeConstants } from "src/app/shared/string-constants";
import { ModalComponent } from "src/app/core/components/modal/modal.component";
import {
  CreateProject,
  GetProject,
  GetUserKey,
  GetProductsList,
  SaveProjectProducts,
} from "src/app/root-store/kwikspace-app-store/actions/floorplanner.action";
import {
  createProject,
  createProjectError,
  createProjectLoading,
  getProject,
  getProjectError,
  getProjectLoading,
  getProductsList,
  getProjectProducts,
  getProjectProductsLoading,
  getProjectProductsError,
} from "src/app/root-store/kwikspace-app-store/selectors/floorplanner.selector";
import {
  CreatePurchaseLineItem,
  CreatePurchaseLineItemConfig,
  GetFpProjectPriceLists,
  GetFpProjectSize,
  GetMatchedPurchasePricing,
} from "src/app/root-store/kwikspace-app-store/actions/purchase.action";
import {
  getCreatedLineItemConfigs,
  getCreatedLineItemConfigsError,
  getCreatedLineItemConfigsLoading,
  getCreatedPurchaseLineItems,
  getCreatedPurchaseLineItemsError,
  getCreatedPurchaseLineItemsLoading,
  getFpProjectSizeList,
  getMatchedPricing,
  getMatchedPricingError,
  getMatchedPricingLoading,
  getPurchasePriceList,
} from "src/app/root-store/kwikspace-app-store/selectors/purchase.selector";
import { getCreatedPurchaseQuote } from "src/app/root-store/kwikspace-app-store/selectors/quote.selector";
import { AppRoutes } from "src/app/shared/endpoints.enum";
import { Router } from "@angular/router";
import { GetTransportLoads } from "src/app/root-store/kwikspace-app-store/actions/transport.action";
import {
  getTransportLoads,
  getTransportLoadsError,
  getTransportLoadsLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/transport.selector";

@Component({
  selector: "app-new-diagram",
  templateUrl: "./new-diagram.component.html",
  styleUrls: ["./new-diagram.component.scss"],
})
export class NewDiagramComponent implements OnInit, OnDestroy {
  private cancelationSubscription = new Subscription();
  private ngUnsubscribe = new Subject();

  @Input() currentUser;
  @Input() category;

  @Output() onProjectSelect = new EventEmitter<any>();

  @ViewChild("floorplannerContainer", { read: ElementRef })
  floorplannerContainer: ElementRef;

  @ViewChild("saveConfirmation", { static: false })
  saveConfirmationModal: ModalComponent;

  @ViewChild("selectUnitSize", { static: false })
  selectUnitSize: ModalComponent;

  createProjectLoading$: Observable<any> =
    this.store.select(createProjectLoading);
  createProjectError$: Observable<any> = this.store.select(createProjectError);

  projectLoading$: Observable<any> = this.store.select(getProjectLoading);
  projectError$: Observable<any> = this.store.select(getProjectError);

  getProjectProductsLoading$: Observable<any> = this.store.select(
    getProjectProductsLoading
  );
  getProjectProductError$: Observable<any> = this.store.select(
    getProjectProductsError
  );

  matchedProductPricingLoading$: Observable<any> = this.store.select(
    getMatchedPricingLoading
  );
  matchedProductPricingError$: Observable<any> = this.store.select(
    getMatchedPricingError
  );

  transportLoadsLoading$: Observable<any> = this.store.select(
    getTransportLoadsLoading
  );
  transportLoadsError$: Observable<any> = this.store.select(
    getTransportLoadsError
  );

  createLineItemLoading$: Observable<any> = this.store.select(
    getCreatedPurchaseLineItemsLoading
  );
  createLineItemError$: Observable<any> = this.store.select(
    getCreatedPurchaseLineItemsError
  );

  createLineItemConfigLoading$: Observable<any> = this.store.select(
    getCreatedLineItemConfigsLoading
  );
  createLineItemConfigError$: Observable<any> = this.store.select(
    getCreatedLineItemConfigsError
  );

  currentProject: FloorplannerProject = null;
  currentProjectProduct: FloorplannerAsset = null;

  isAuthenticating: boolean = false;
  authenticatingError: boolean = false;

  matchedProducts;
  matchedPricings;

  transportLoads: any[] = [];
  createdQuote: any = null;
  quoteLineItemProducts: any[] = [];
  addedLineItem;
  unitSizes: any[] = [];
  selectedSize = null;
  selectForm: FormGroup;
  purchasePriceSize: any = null;

  constructor(
    private store: Store<RootStoreState.RootState>,
    private router: Router,
    private floorplannerService: FloorplannerService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    this.createProject();

    this.store
      .select(getCreatedPurchaseQuote)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.createdQuote = quote;
          if (
            this.createdQuote.shipping_province != null &&
            this.createdQuote.shipping == true
          ) {
            this.getTransportLoads();
          }
        }
      });
    this.selectForm = this.formBuilder.group({
      selectedSize: [null, [Validators.required]],
    });

    this.store
      .select(getFpProjectSizeList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((cat_sizes) => {
        if (cat_sizes && cat_sizes.length > 0) {
          this.unitSizes = cat_sizes;
        }
      });

    this.store.dispatch(new GetFpProjectSize({}));
  }

  getTransportLoads() {
    this.store.dispatch(new GetTransportLoads());

    this.store
      .select(getTransportLoads)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((loads: any) => {
        if (loads && loads.length > 0) {
          this.transportLoads = loads.filter(
            (loadSize) => loadSize.available == true
          );
          this.transportLoads = this.transportLoads.filter(
            (loadSize) => loadSize.hasPurchase == true
          );
        }
      });
  }

  getUserAuth() {
    const id = this.currentProject.userId;
    this.store.dispatch(new GetUserKey(id));

    const userKey = this.store.pipe(
      select((state: any) => state.kwikspaceApp.floorplanner)
    );
    this.cancelationSubscription.add(
      userKey.subscribe(async (data) => {
        this.isAuthenticating = data.keyLoading;
        this.authenticatingError = data.keyError;

        if (data.key && !data.keyLoading && !data.keyError) {
          let permissions: any = {
            user: {
              id: data.key.userId,
              permissions: ["save"],
              auth_token: data.key.token,
            },
          };

          const FP_API: FloorplannerAPI =
            await this.floorplannerService.createFloorplannerView(
              this.currentProject.id,
              this.floorplannerContainer.nativeElement,
              permissions
            );

          if (FP_API) {
            let designId = this.currentProject.floors[0].designs[0].id;
            this.floorplannerService.setDiagramDesign(designId);
          }

          (window as any).fpEditor.onUpdated = (data) => {
            // callback runs on init, this check so see actual change is made
            const isUpdated =
              data.state.items.length ||
              data.state.walls.length ||
              data.state.areas.length;
            if (isUpdated) {
              this.saveConfirmationModal.open();
            }
          };
        }
      })
    );
  }

  createProject() {
    let random = Math.floor(1000 + Math.random() * 9000);
    let payload = {
      name: "Shell-" + this.currentUser.id + "_" + random,
      description: "This is a custom user project",
      external_identifier: null,
    };

    this.store.dispatch(new CreateProject(payload));

    this.store
      .select(createProject)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (res: FloorplannerProject) => {
        if (res) {
          this.getProject(res.floorplanner_project_id);
        }
      });
  }

  getProject(projectId) {
    this.store.dispatch(new GetProject(projectId));

    this.store
      .select(getProject)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.currentProject = res;
          this.getUserAuth();
        }
      });
  }

  saveProjectProducts(projectId) {
    this.store.dispatch(new SaveProjectProducts(projectId));

    this.store
      .select(getProjectProducts)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          if (res.products && res.products.length > 0) {
            this.saveConfirmationModal.close();
            this.selectUnitSize.close();
            this.getPricing();
            this.getMatchedProductsPricing(res.products);
          }
        }
      });
  }
  getPricing() {
    const payload = {
      category: `?category__name=${this.category}`,
      size: `&size__size=${this.selectForm.value.selectedSize}`,
    };

    this.store.dispatch(new GetFpProjectPriceLists(payload));

    this.store
      .select(getPurchasePriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.purchasePriceSize = res;
        }
      });
  }

  saveProject() {
    this.saveProjectProducts(this.currentProject.id);
  }

  confirmSave() {
    this.saveConfirmationModal.open();
  }
  confirmUnitSizes() {
    this.selectUnitSize.open();
  }

  closeSaveConfModal() {
    this.saveConfirmationModal.close();
  }

  getMatchedProductsPricing(products) {
    let filteredProducts = [];
    let mappedProducts = [];

    products.forEach((product) => {
      let productAdded = filteredProducts.some((el) => el.sku === product.sku);

      if (productAdded) {
        const idx = filteredProducts.findIndex((x) => x.sku === product.sku);
        filteredProducts[idx].qty += 1;
      } else {
        const _product = { ...product, qty: 1, price: 0 };
        const sku = product.sku.toString();

        filteredProducts.push(_product);
        mappedProducts.push(sku);
      }
    });

    // filteredProducts.forEach((product) => {
    //   let sku = product.sku.toString();
    //   mappedProducts.push(sku);
    // });

    let payload = {
      products: mappedProducts,
    };

    this.store.dispatch(new GetMatchedPurchasePricing(payload));

    this.store
      .select(getMatchedPricing)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((pricelist: any) => {
        if (pricelist && pricelist.length > 0) {
          this.matchedProducts = pricelist;
          this.mapQuantities(filteredProducts);
        }
      });
  }

  mapQuantities(filteredProducts) {
    filteredProducts.forEach((product, index) => {
      this.matchedProducts.some((el) => {
        if (el.floor_planner_sku === product.sku) {
          filteredProducts[index].price = el.purchase_price;
        }
      });
    });

    this.quoteLineItemProducts = filteredProducts;

    this.saveConfirmationModal.close();
    this.selectUnitSize.close();

    this.saveLineItems();
  }

  saveLineItems() {
    this.saveLineItem(this.currentProject);

    this.store
      .select(getCreatedPurchaseLineItems)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((items) => {
        if (items && items.length > 0) {
          this.addedLineItem = items[0];

          if (this.quoteLineItemProducts.length == 0) {
            const route = `${
              AppRoutes.PWA + "quotes/edit/purchase/" + this.createdQuote.id
            }`;
            this.router.navigate([route]);
          } else {
            this.saveProducts();
          }
        }
      });
  }

  saveLineItem(item) {
    let payload = {
      name: item.name,
      unit: "Unit 1",
      description: item.description,
      product_configurations: [],
      delivery_charge: item.transport ? item.transport.id : null,
      delivery_charge_optional: item.transportJoin
        ? item.transportJoin.id
        : null,
      delivery_charge_variation: item.transportExtra
        ? item.transportExtra.id
        : null,
      quantity: 1,
      quote: this.createdQuote.id,
      floor_planner_sku: item.id,
      unit_size: this.selectedSize.size.size,
      unit_price: '123',
      total_price: item.price * item.quantity,
      thumbnail: item.thumbnail,
      thumbnail_three_d: item.thumbnail3d,
    };

    this.store.dispatch(new CreatePurchaseLineItem(payload));
  }

  saveProducts() {
    let addon = this.quoteLineItemProducts[0];
    this.saveProduct(addon);

    this.store
      .select(getCreatedLineItemConfigs)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((products) => {
        if (products) {
          let productsAdded = products.length;

          if (this.quoteLineItemProducts.length == productsAdded) {
            const route = `${
              AppRoutes.PWA + "quotes/edit/purchase/" + this.createdQuote.id
            }`;
            this.router.navigate([route]);
          } else {
            if (productsAdded > 0) {
              let itemIdx = this.quoteLineItemProducts[productsAdded];
              this.saveProduct(itemIdx);
            }
          }
        }
      });
  }

  saveProduct(addon) {
    const payload = {
      name: addon.name,
      asset: "",
      quantity: addon.qty,
      unit_price: 0,
      total_price: 0,
      purchase_line_item: this.addedLineItem.id,
    };

    this.store.dispatch(new CreatePurchaseLineItemConfig(payload));
  }

  ngOnDestroy() {
    this.cancelationSubscription.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
